import React, { useEffect, useState } from 'react'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { supabase } from '@/lib/supabase'
import { compareVersions } from '@/lib/versionMath'
import { Text } from '@/components/ui/text'
import { Button } from '@/components/ui/button'
import { ShieldAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Browser } from '@capacitor/browser'

export function ForceUpdateGate({ children }: { children: React.ReactNode }) {
    const [isBlocked, setIsBlocked] = useState(false)
    const { t } = useTranslation()

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return

        const checkVersion = async () => {
            try {
                // 3 second timeout to prevent offline locking
                const timeoutPromise = new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('timeout')), 3000)
                )

                const fetchPromise = supabase
                    .from('app_config')
                    .select('minimum_required_version')
                    .single()

                const { data, error } = await Promise.race([fetchPromise, timeoutPromise]) as any

                if (error || !data) return

                const minVersion = data.minimum_required_version
                const appInfo = await App.getInfo()
                const currentVersion = appInfo.version

                if (compareVersions(minVersion, currentVersion) > 0) {
                    setIsBlocked(true)
                }
            } catch (err) {
                // Fail open on network errors or timeout
                console.error("Failed to check app version", err)
            }
        }

        checkVersion()
    }, [])

    const handleUpdate = async () => {
        // Fallback store URLs, to be updated with real ones
        const url = Capacitor.getPlatform() === 'ios' 
            ? 'https://apps.apple.com/app/shuvo' 
            : 'https://play.google.com/store/apps/details?id=com.shuvo.app'
        
        await Browser.open({ url })
    }

    return (
        <>
            {children}
            
            {isBlocked && (
                <div className="fixed inset-0 z-[9999] bg-background flex flex-col items-center justify-center p-6 animate-in fade-in duration-300">
                    <ShieldAlert className="w-16 h-16 text-destructive mb-6" />
                    
                    <Text variant="headline-medium" className="text-center mb-4">
                        {t('common.update_required', 'Update Required')}
                    </Text>
                    
                    <Text variant="body-medium" className="text-center text-muted-foreground mb-8 max-w-[300px]">
                        {t('common.update_required_desc', 'You are using an older version of Shuvo. A critical update is required to continue.')}
                    </Text>
                    
                    <Button 
                        className="w-full max-w-[280px]"
                        size="lg"
                        onClick={handleUpdate}
                    >
                        {t('common.update_now', 'Update Now')}
                    </Button>
                </div>
            )}
        </>
    )
}
